import { create } from 'zustand';
import { throttle } from 'lodash';
import { trackEvent } from '~/services/analytics/trackEvent';
import { isStepSent, markStepAsSent } from '~/services/analytics/utils/sentStepsStorage';
import { ENV_IS_ENABLED_ROISTAT, ENV_IS_ENABLED_YANDEX_METRIKA } from '~/configs/consts';
import { isDev } from '~/utils';
import { OrgTypeEnum } from '~/typings/types';
import { PREFIX_BUILD_EVENT_NAME } from '~/services/analytics/utils/buildEventName';

interface AnalyticsState {
  clientId: string | null;
  orgType: OrgTypeEnum | null;
  currentStep: number;
  appVersion: 'lite' | 'default' | 'new-ui';
  platform: 'desktop' | 'mobile';
  isInitialized: boolean;
  /** True only when the client started the session from step 0 (not a returning user). Set once on load. */
  isNewSession: boolean;
  setIsNewSession: (value: boolean) => void;

  initialize: (params: {
    clientId: string;
    orgType: OrgTypeEnum;
    step: number;
    appVersion?: 'lite' | 'default' | 'new-ui';
    platform?: 'desktop' | 'mobile';
  }) => void;
  trackStep: (stepName: string, extra?: Record<string, any>) => void;
  trackEvent: (eventName: string, extra?: Record<string, any>) => void;
  reset: () => void;
}

const getAnalyticsConfig = () => ({
  enabled: {
    yandex: ENV_IS_ENABLED_YANDEX_METRIKA,
    roistat: ENV_IS_ENABLED_ROISTAT,
  },
  isDev,
});

const throttledStepEvent = throttle(trackEvent, 2000, { leading: true, trailing: false });

export const useAnalyticsStore = create<AnalyticsState>((set, get) => ({
  clientId: null,
  orgType: null,
  currentStep: 0,
  appVersion: 'new-ui',
  platform: 'desktop',
  isInitialized: false,
  isNewSession: false,
  setIsNewSession: (value) => set({ isNewSession: value }),

  initialize: ({ clientId, orgType, step, appVersion = 'new-ui', platform = 'desktop' }) => {
    console.log('NEW AnalyticsProvider initialize')
    set({
      clientId,
      orgType,
      currentStep: step,
      appVersion,
      platform,
      isInitialized: true,
    });

    const INIT_DRAFT_FLAG = `analytics_init_draft_sent_${clientId}`;
    const alreadySent = localStorage.getItem(INIT_DRAFT_FLAG);

    if (!alreadySent) {
      const throttledInitEvent = throttle(() => {
        trackEvent({
          appVersion,
          clientId,
          step,
          eventName: `${PREFIX_BUILD_EVENT_NAME}${platform}_page-first-load`,
          extra: { timestamp: Date.now() },
          config: getAnalyticsConfig(),
        });
        trackEvent({
          appVersion,
          clientId,
          step,
          eventName: `${PREFIX_BUILD_EVENT_NAME}page-first-load`,
          extra: { timestamp: Date.now() },
          config: getAnalyticsConfig(),
        });
      }, 3000, { leading: true, trailing: false });

      throttledInitEvent();
      localStorage.setItem(INIT_DRAFT_FLAG, 'true');
    }
  },

  trackStep: (stepName: string, extra?: Record<string, any>) => {
    const state = get();
    if (!state.isInitialized || !state.clientId) {
      console.warn('[Analytics] Not initialized, skipping trackStep');
      return;
    }

    if (isStepSent(state.clientId, stepName)) {
      return;
    }

    throttledStepEvent({
      appVersion: state.appVersion,
      clientId: state.clientId,
      step: state.currentStep,
      eventName: stepName,
      extra: { timestamp: Date.now(), ...extra },
      config: getAnalyticsConfig(),
    });

    markStepAsSent(state.clientId, stepName);
  },

  trackEvent: (eventName: string, extra?: Record<string, any>) => {
    const state = get();
    if (!state.isInitialized || !state.clientId) {
      console.warn('[Analytics] Not initialized, skipping trackEvent');
      return;
    }

    throttledStepEvent({
      appVersion: state.appVersion,
      clientId: state.clientId,
      step: state.currentStep,
      eventName,
      extra: { timestamp: Date.now(), ...extra },
      config: getAnalyticsConfig(),
    });

  },

  reset: () => {
    set({
      clientId: null,
      orgType: null,
      currentStep: 0,
      appVersion: 'new-ui',
      platform: 'desktop',
      isInitialized: false,
    });
  },
}));

export const selectIsAnalyticsInitialized = (state: AnalyticsState) => state.isInitialized;
export const selectCurrentStep = (state: AnalyticsState) => state.currentStep;
